import {
  FLOOR_PLAN_KIND_LABEL,
  type FloorPlanKind,
} from '@obraplan/core/domain/takeoff/floor-plan';
import type { Result } from '@obraplan/core/domain/shared/result';
import { UNIT_TYPE_KIND_LABEL } from '@obraplan/core/domain/takeoff/unit-type';
import type { TypologyDraft, WorkspaceDraft } from './typology-draft';

/**
 * A área única da fundação e da contenção (07/10/2026).
 *
 * O desenho de Fundação ou de Contenção não tem apto, vaga nem circulação: tem uma área só,
 * a da base do prédio. No modelo ela segue sendo uma tipologia `AREA_COMUM` com uma posição
 * no desenho — é onde as regras de peça (estaca, bloco, baldrame, radier, contenção) e a
 * conferência do servidor já olham. Mudou o que a pessoa vê, e não o que é gravado.
 */

/** Os desenhos que têm uma área só, em vez de apto, vaga e área comum. */
export function temAreaUnica(floorKind: FloorPlanKind): boolean {
  return floorKind === 'FUNDACAO' || floorKind === 'CONTENCAO';
}

/** Esta tipologia é a área única de um desenho de Fundação ou de Contenção. */
export function ehAreaUnica(typology: Pick<TypologyDraft, 'kind' | 'floorKind'>): boolean {
  return typology.kind === 'AREA_COMUM' && temAreaUnica(typology.floorKind);
}

const NOME_PADRAO_DE_AREA_COMUM = /^Área comum \d+$/;

/**
 * O nome que a tela mostra. A área única de obra antiga se chama "Área comum 1", e é isso que
 * a pessoa não deve ler no desenho da fundação; um nome dado por ela (ou o do desenho, que é
 * o que nasce hoje) vai como está.
 */
export function nomeDaTipologia(typology: Pick<TypologyDraft, 'kind' | 'floorKind' | 'name'>) {
  if (ehAreaUnica(typology) && NOME_PADRAO_DE_AREA_COMUM.test(typology.name)) {
    return `Área única da ${FLOOR_PLAN_KIND_LABEL[typology.floorKind]}`;
  }
  return typology.name;
}

/** "Área comum" / "Apartamento" / "Vaga" — e "Área única" onde a área comum é a única. */
export function rotuloDoTipo(typology: Pick<TypologyDraft, 'kind' | 'floorKind'>): string {
  return ehAreaUnica(typology) ? 'Área única' : UNIT_TYPE_KIND_LABEL[typology.kind];
}

/** Os nomes de tipologia já em uso na obra — o servidor recusa nome repetido. */
export function espinhaNomes(workspace: Pick<WorkspaceDraft, 'typologies'>): ReadonlySet<string> {
  return new Set(workspace.typologies.map((typology) => typology.name));
}

/** O nome da área única de um desenho: o do desenho, ou um livre se ele já estiver em uso. */
export function nomeDaAreaUnica(nomeDoDesenho: string, usados: ReadonlySet<string>): string {
  const base = nomeDoDesenho.trim();
  if (base !== '' && !usados.has(base)) return base;
  for (let n = 1; n < 100; n += 1) {
    const nome = `Área única ${String(n)}`;
    if (!usados.has(nome)) return nome;
  }
  return `Área única ${String(usados.size + 1)}`;
}

/**
 * Cria o desenho e, se ele é de Fundação ou de Contenção, a área única junto — a mesma
 * tipologia `AREA_COMUM` que o botão "Área comum" cria, com a posição dela no desenho. Para no
 * primeiro passo que o servidor recusar, e a mensagem dele é a que a pessoa lê.
 */
export async function criarDesenhoComAreaUnica<E>(
  kind: FloorPlanKind,
  passos: {
    readonly criarDesenho: () => Promise<Result<{ readonly id: string }, E>>;
    readonly criarAreaUnica: (floorPlanId: string) => Promise<Result<unknown, E>>;
  },
): Promise<Result<{ readonly id: string }, E>> {
  const desenho = await passos.criarDesenho();
  if (!desenho.ok || !temAreaUnica(kind)) return desenho;
  const areaUnica = await passos.criarAreaUnica(desenho.value.id);
  return areaUnica.ok ? desenho : areaUnica;
}

/**
 * Quantas peças moram na tipologia: estacas, blocos, baldrames, radier, lajes, escadas, muros e
 * pilares de contenção. É o que sai junto quando ela é removida.
 */
export function pecasDaTipologia(
  typology: Pick<
    TypologyDraft,
    'pieces' | 'slabs' | 'stairs' | 'retainingWalls' | 'retainingColumns'
  >,
): number {
  return (
    (typology.pieces?.length ?? 0) +
    (typology.slabs?.length ?? 0) +
    (typology.stairs?.length ?? 0) +
    (typology.retainingWalls?.length ?? 0) +
    (typology.retainingColumns?.length ?? 0)
  );
}
